import { useEffect, useState } from 'react'
import { Header } from '../../../../../components/Header'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner } from '../../../../../components/Commons/Spinner/Spinner'
import GenericPromises from '../../../../../api/GenericPromises'
import { PrimaryButton } from '../../../../../theme/buttons'
import { GridColDef, GridRowsProp } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { Warehouse } from '../../../../../interfaces/Stocks/Warehouses/warehouses'
import useSnackBar from '../../../../../components/Commons/SnackBar/useSnackBar'
import { ErrorDisplay } from '../../../../../components/Commons/Error/ErrorDisplay'
import DataTableSticky from '../../../../../components/Tables/GridTableMaterialUI/DataTableSticky'
import { Box } from '@mui/material'
import { useCompanyStore } from '../../../../../store/AuthStore'
import { FilterTable } from '../../../../../components/Filter'
import { useGridPreferences } from '../../../../../hooks/useGridPreferences'
import { usePreferenceTypesStore } from '../../../../../store/Preferences/PreferencesStore'
import { PreferenceType } from '../../../../../interfaces/Commons/Preferences/preferences'
import { useColumnsWarehouses } from './columns'

export const TableWarehouses = () => {
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const location = useLocation();
  const { companyState } = useCompanyStore();
  const { GenericGetResource } = GenericPromises();
  const { showSnackBar, SnackbarComponent } = useSnackBar();
  const { columnsWarehouses } = useColumnsWarehouses();
  const {
    LoadColumnsOrder,
    LoadColumnsVisible,
    LoadColumnsSort,
    VerifyPreferenceOrderByResourceRoute,
    VerifyPreferenceVisualByResourceRoute,
    VerifyPreferenceSortByResourceRoute,
  } = useGridPreferences();
  const { preferenceTypes } = usePreferenceTypesStore.getState();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
  const [isLoadedMyLocalPreferences, setIsLoadedMyLocalPreferences] = useState(false);
  const [warehousesData, setWarehousesData] = useState<GridRowsProp>([]);
  const [mySortPreference, setMySortPreference] = useState([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const initFilter = '/warehouses?filter=';
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [myPreferences, setMyPreferences] = useState({});
  const [columns, setColumns] = useState<GridColDef<Warehouse>[]>([]);

  const myCustomColumns: GridColDef<any>[] = [];

  const loadTablePreferences = async () => {
    // get preferencetypes in Store
    let hasPreferenceOrder: boolean = false;
    // if exists preferencetypes
    if (preferenceTypes.length) {
      // if exists order preferences
      let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
      if (myPreferencesColumnsOrder) {
        try {
          const preference = await VerifyPreferenceOrderByResourceRoute(
            "/warehouses",
            myPreferencesColumnsOrder.preference_type_id ?? 0
          );
          if (preference) {
            let myColumns = await LoadColumnsOrder("/warehouses", myCustomColumns, preference?.preference_value);
            setColumns(myColumns);
            hasPreferenceOrder = true;
          }
        } catch (error) {
          showSnackBar(`Error fetching column order preferences:${error}`, "error");
        }
      }

      let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
      // if exists visual preferences
      if (myPreferencesColumnsVisual) {
        try {
          const preference = await VerifyPreferenceVisualByResourceRoute(
            "/warehouses",
            myPreferencesColumnsVisual.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsVisible("/warehouses", preference);
            setMyPreferences(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column visual preferences:", error);
        }
      }

      let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
      // if exists sort preferences
      if (myPreferencesColumnsSort) {
        try {
          const preference = await VerifyPreferenceSortByResourceRoute(
            "/warehouses",
            myPreferencesColumnsSort.preference_type_id ?? 0
          );
          if (preference) {
            let myLocalPreferences = await LoadColumnsSort("/warehouses", preference);
            setMySortPreference(myLocalPreferences);
          }
        } catch (error) {
          console.error("Error fetching column sort preferences:", error);
        }
      }

    }
    if (!hasPreferenceOrder) {
      setColumns(columnsWarehouses);
    }
    setIsLoadedMyLocalPreferences(true);
  }

  const GetWarehouses = () => {
    GenericGetResource(`/warehouses`)
      .then((response) => {
        setWarehousesData(response.data.data);
        setIsLoadingData(false);
      });
  }

  useEffect(() => {
    if (isLoadedMyLocalPreferences && warehousesData) {
      setDataLoaded(true);
      setIsLoadingDataTable(true);
    }
  }, [warehousesData, isLoadedMyLocalPreferences]);

  useEffect(() => {
    document.title = `${companyState?.comercial_name} - ${t("warehouses.title")}`;

    const fetchData = async () => {
      try {
        await loadTablePreferences();
        GetWarehouses();
      } catch (err: any) {
        setErrorMessage(err);
        setHasError(true);
        showSnackBar(err, 'error');
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!dataLoaded && !hasError && <Spinner />}
      {hasError &&
        <ErrorDisplay
          errorMessage={errorMessage}
        />
      }
      {dataLoaded && <div className='screen-container'>
        <Header
          title={t("warehouses.title")}
          child={
            <div className='d-flex flex-row-reverse'>
              <>
                <PrimaryButton
                  variant='outlined'
                  className="my-1"
                  onClick={() => { navigate("add", { state: { ...location.state } }) }}
                >
                  {t("generic.buttons.add")}
                </PrimaryButton>
                <FilterTable
                  onFilterEmpty={GetWarehouses}
                  setIsLoadingDataTable={setIsLoadingData}
                  setDataTable={setWarehousesData}
                  showSnackBar={showSnackBar}
                  columnsTable={columns}
                  initFilter={initFilter}
                  resourceRoute="/warehouses"
                />
              </>
            </div>
          }
        />

        {isLoadingDataTable &&
          <Box sx={{ height: 'calc(100vh - 180px)', width: '100%' }}>
            <DataTableSticky
              columns={columns}
              setColumns={setColumns}
              data={warehousesData}
              entityId={"warehouse_id"}
              entity={`Warehouses`}
              preferences={myPreferences}
              namePreferences={"/warehouses"}
              nameOrderColumns={"/warehouses"}
              isLoading={isLoadingData}
              sortPreference={mySortPreference}
            />
          </Box>
        }

        <SnackbarComponent />
      </div>
      }
    </>
  )
}
